/**
 * CostiVariabili — spese una tantum (gasolio, materiali, fornitori, parcelle,
 * ore lavoratori, ecc.) legate opzionalmente a un cantiere.
 *
 * Rosso = previsto/futuro, nero = pagato/confermato. Confluisce nel Cash Flow.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, CheckCircle2, Clock, AlertTriangle, Edit3, X, Filter, FileSpreadsheet, FileText } from "lucide-react";
import api from "@/api";
import RipartizioneMensile from "@/pages/costi/RipartizioneMensile";
const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CostiVariabili() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [kpiDetail, setKpiDetail] = useState(null);

  const { data: categorie = [] } = useQuery({
    queryKey: ["costi-variabili-categorie"],
    queryFn: () => api.get("/costi-variabili/categorie").then((r) => r.data),
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-min"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
  });
  const { data: items = [], refetch } = useQuery({
    queryKey: ["costi-variabili", filterCategoria, filterStato],
    queryFn: () => api.get("/costi-variabili", { params: {
      categoria: filterCategoria || undefined,
      stato: filterStato || undefined,
    }}).then((r) => r.data),
  });
  const { data: stats } = useQuery({
    queryKey: ["costi-variabili-stats"],
    queryFn: () => api.get("/costi-variabili/statistiche").then((r) => r.data),
    refetchInterval: 15000,
  });

  const saveMut = useMutation({
    mutationFn: (payload) => editing
      ? api.patch(`/costi-variabili/${editing.costo_id}`, payload).then((r) => r.data)
      : api.post("/costi-variabili", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success(editing ? "Voce aggiornata" : "Voce creata");
      qc.invalidateQueries({ queryKey: ["costi-variabili"] });
      qc.invalidateQueries({ queryKey: ["costi-variabili-stats"] });
      qc.invalidateQueries({ queryKey: ["cash-flow"] });
      setShowForm(false); setEditing(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Salvataggio fallito"),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/costi-variabili/${id}`),
    onSuccess: () => {
      toast.success("Voce eliminata — cash-flow aggiornato");
      qc.invalidateQueries({ queryKey: ["costi-variabili"] });
      qc.invalidateQueries({ queryKey: ["costi-variabili-stats"] });
      qc.invalidateQueries({ queryKey: ["cash-flow"] });
    },
    onError: (e) => {
      const status = e?.response?.status ? `HTTP ${e.response.status} · ` : "";
      toast.error(`Eliminazione fallita — ${status}${e?.response?.data?.detail || e.message || "Errore"}`);
    },
  });

  const pagaMut = useMutation({
    mutationFn: (id) => api.post(`/costi-variabili/${id}/paga`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Voce marcata come pagata");
      qc.invalidateQueries({ queryKey: ["costi-variabili"] });
      qc.invalidateQueries({ queryKey: ["cash-flow"] });
    },
  });

  const visibleItems = useMemo(() => {
    if (!kpiDetail) return items;
    return items.filter((i) => {
      if (i.source === "richiesta_materiali") return false;
      if (kpiDetail === "previste") return i.source === "manuale" && i.stato !== "pagato";
      if (kpiDetail === "pagate") return i.stato === "pagato";
      if (kpiDetail === "scadute") return i.source === "manuale" && i.stato !== "pagato" && Boolean(i.data_scadenza) && i.data_scadenza < todayIso();
      return true;
    });
  }, [items, kpiDetail]);

  const total = useMemo(() => visibleItems.reduce((s, i) => s + Number(i.stato === "pagato" ? (i.importo_totale_effettivo ?? i.importo_totale_previsto ?? 0) : (i.importo_totale_previsto ?? 0)), 0), [visibleItems]);

  return (
    <div className="space-y-6" data-testid="page-costi-variabili">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Amministrazione</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
            <Wallet className="w-7 h-7 text-slate-500" /> Costi Variabili
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Spese una tantum (gasolio, materiali, parcelle, ore lavoratori, ecc.).
            Confluiscono nel Cash Flow — <b className="text-rose-600">rosso</b> quando previste, <b>nere</b> quando pagate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${REACT_APP_BACKEND_URL}/api/export/costi-variabili/excel?${new URLSearchParams({ ...(filterCategoria ? { categoria: filterCategoria } : {}), ...(filterStato ? { stato: filterStato } : {}) })}`}
            target="_blank" rel="noreferrer"
            data-testid="btn-export-cv-excel"
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm">
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Excel
          </a>
          <a
            href={`${REACT_APP_BACKEND_URL}/api/export/costi-variabili/pdf?${new URLSearchParams({ ...(filterCategoria ? { categoria: filterCategoria } : {}), ...(filterStato ? { stato: filterStato } : {}) })}`}
            target="_blank" rel="noreferrer"
            data-testid="btn-export-cv-pdf"
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm">
            <FileText className="w-4 h-4 text-rose-700" /> PDF
          </a>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="btn-nuovo-costo-var"
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm"
          >
            <Plus className="w-4 h-4" /> Nuova voce
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Voci totali" value={stats?.totale_voci ?? 0} tone="slate" />
        <KpiCard label="Previste (rosso)" value={euro(stats?.totale_previsto)} tone="rose" active={kpiDetail === "previste"} onClick={() => { setFilterCategoria(""); setFilterStato(""); setKpiDetail(kpiDetail === "previste" ? null : "previste"); }} />
        <KpiCard label="Pagate (nero)" value={euro(stats?.totale_pagato)} tone="slate-dark" active={kpiDetail === "pagate"} onClick={() => { setFilterCategoria(""); setFilterStato(""); setKpiDetail(kpiDetail === "pagate" ? null : "pagate"); }} />
        <KpiCard label="Scadute non pagate" value={stats?.scaduti_non_pagati ?? 0} tone="amber" alarm={(stats?.scaduti_non_pagati ?? 0) > 0} active={kpiDetail === "scadute"} onClick={() => { setFilterCategoria(""); setFilterStato(""); setKpiDetail(kpiDetail === "scadute" ? null : "scadute"); }} />
      </div>

      {kpiDetail && <button type="button" onClick={() => setKpiDetail(null)} className="text-sm font-semibold text-blue-700 hover:underline">Dettaglio {kpiDetail === "previste" ? "Previste" : kpiDetail === "pagate" ? "Pagate" : "Scadute non pagate"} · Mostra tutte</button>}
      {/* Filtri */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <select value={filterCategoria} onChange={(e) => { setFilterCategoria(e.target.value); setKpiDetail(null); }}
          data-testid="filter-categoria"
          className="border border-slate-300 rounded-sm px-2 py-1 bg-white">
          <option value="">Tutte le categorie</option>
          {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStato} onChange={(e) => { setFilterStato(e.target.value); setKpiDetail(null); }}
          data-testid="filter-stato"
          className="border border-slate-300 rounded-sm px-2 py-1 bg-white">
          <option value="">Tutti gli stati</option>
          <option value="previsto">🔴 Previsto</option>
          <option value="pagato">⚫ Pagato</option>
        </select>
        <span className="ml-auto text-slate-500">
          {visibleItems.length} voci · totale imponibile: <b className="text-slate-900">{euro(total)}</b>
        </span>
      </div>

      {/* Tabella */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="table-costi-variabili">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Scadenza</Th>
                <Th>Descrizione</Th>
                <Th>Categoria</Th>
                <Th>Fornitore</Th>
                <Th>Cantiere</Th>
                <Th className="text-right">Importo</Th>
                <Th className="text-center">Stato</Th>
                <Th className="text-right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400 italic">Nessuna voce inserita</td></tr>
              )}
              {visibleItems.map((i) => {
                const isPast = i.data_scadenza < todayIso();
                const isPagato = i.stato === "pagato";
                const scaduta = isPast && !isPagato;
                const isBolla = i.source === "bolla";
                return (
                  <tr key={i.costo_id} className={`border-t border-slate-100 hover:bg-slate-50 ${isPagato ? "text-slate-900" : "text-rose-700"} ${scaduta ? "bg-rose-50" : ""} ${isBolla ? "bg-blue-50/40" : ""}`}
                      data-testid={`row-costo-${i.costo_id}`}>
                    <Td>{i.data_scadenza}</Td>
                    <Td className="font-semibold">
                      {isBolla && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mr-2 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 rounded-sm border border-blue-300" title="Voce generata da una bolla di cantiere/manutenzione">
                          📄 Bolla
                        </span>
                      )}
                      {i.descrizione}
                    </Td>
                    <Td>{i.categoria}</Td>
                    <Td>{i.fornitore || "—"}</Td>
                    <Td>{cantieri.find((c) => c.cantiere_id === i.cantiere_id)?.nome || "—"}</Td>
                    <Td className="text-right font-mono tabular-nums">
                      {isPagato ? euro(i.importo_totale_effettivo) : euro(i.importo_totale_previsto)}
                    </Td>
                    <Td className="text-center">
                      {isPagato ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />pagato</span>
                        : scaduta ? <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><AlertTriangle className="w-3 h-3" />scaduta</span>
                        : <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />previsto</span>}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {isBolla ? (
                          <a
                            href="/bolle"
                            title="Gestisci in Bolle & Materiali"
                            className="text-[11px] text-blue-700 hover:underline font-semibold px-2"
                          >
                            Vai a Bolle →
                          </a>
                        ) : (
                          <>
                            {!isPagato && (
                              <button onClick={() => pagaMut.mutate(i.costo_id)}
                                title="Marca come pagata"
                                data-testid={`btn-paga-${i.costo_id}`}
                                className="p-1 text-emerald-700 hover:bg-emerald-100 rounded-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => { setEditing(i); setShowForm(true); }}
                              title="Modifica"
                              data-testid={`btn-edit-${i.costo_id}`}
                              className="p-1 text-slate-600 hover:bg-slate-100 rounded-sm">
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                            <button onClick={() => {
                                if (window.confirm(
                                  `Eliminare la voce di costo "${i.descrizione}"?\n\n` +
                                  `Categoria: ${i.categoria}\n` +
                                  `Importo: ${euro(isPagato ? i.importo_totale_effettivo : i.importo_totale_previsto)}\n` +
                                  `Scadenza: ${i.data_scadenza}\n\n` +
                                  `Il cash flow verrà ricalcolato automaticamente.`
                                )) delMut.mutate(i.costo_id);
                              }}
                              disabled={delMut.isPending}
                              title="Elimina voce"
                              data-testid={`btn-del-${i.costo_id}`}
                              className="p-1 text-rose-700 hover:bg-rose-100 rounded-sm disabled:opacity-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ripartizione mensile con export PDF */}
      <RipartizioneMensile variante="variabili" />

      {showForm && (
        <FormModal
          initial={editing}
          categorie={categorie}
          cantieri={cantieri}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={(payload) => saveMut.mutate(payload)}
          submitting={saveMut.isPending}
        />
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left p-2 font-bold uppercase tracking-widest text-[10px] text-slate-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`p-2 ${className}`}>{children}</td>;

function KpiCard({ label, value, tone = "slate", alarm = false, onClick, active = false }) {
  const toneClasses = {
    slate: "bg-slate-100 text-slate-700",
    "slate-dark": "bg-slate-900 text-white",
    rose: "bg-rose-100 text-rose-800",
    amber: alarm ? "bg-amber-500 text-white animate-pulse" : "bg-amber-100 text-amber-800",
  }[tone];
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={`p-3 rounded-sm w-full text-left cursor-pointer ${active ? "ring-2 ring-blue-600" : ""} ${toneClasses}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-black tabular-nums mt-1">{value}</div>
    </button>
  );
}

function FormModal({ initial, categorie, cantieri, onClose, onSave, submitting }) {
  // Anagrafica fornitori per il datalist "Fornitore"
  const { data: fornitori = [] } = useQuery({
    queryKey: ["fornitori-anagrafica"],
    queryFn: () => api.get("/entita/fornitori").then((r) => r.data),
    staleTime: 60_000,
  });
  const [form, setForm] = useState({
    descrizione: initial?.descrizione || "",
    categoria: initial?.categoria || "Altro",
    cantiere_id: initial?.cantiere_id || "",
    fornitore: initial?.fornitore || "",
    importo_imponibile: initial?.importo_imponibile || 0,
    aliquota_iva: initial?.aliquota_iva ?? 22,
    data_scadenza: initial?.data_scadenza || todayIso(),
    importo_effettivo: initial?.importo_effettivo ?? "",
    data_pagamento: initial?.data_pagamento || "",
    metodo_pagamento: initial?.metodo_pagamento || "",
    note: initial?.note || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      cantiere_id: form.cantiere_id || null,
      importo_imponibile: Number(form.importo_imponibile) || 0,
      aliquota_iva: Number(form.aliquota_iva) || 22,
      importo_effettivo: form.importo_effettivo === "" ? null : Number(form.importo_effettivo),
      data_pagamento: form.data_pagamento || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-3"
        data-testid="form-costo-variabile">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-black">{initial ? "Modifica voce" : "Nuova voce di costo variabile"}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <FormRow label="Descrizione">
          <input required value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)}
            data-testid="input-descrizione"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </FormRow>
        <div className="grid md:grid-cols-2 gap-3">
          <FormRow label="Categoria">
            <select value={form.categoria} onChange={(e) => set("categoria", e.target.value)}
              data-testid="input-categoria"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormRow>
          <FormRow label="Cantiere (facoltativo)">
            <select value={form.cantiere_id} onChange={(e) => set("cantiere_id", e.target.value)}
              data-testid="input-cantiere"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">— Nessuno —</option>
              {cantieri.map((c) => <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>)}
            </select>
          </FormRow>
          <FormRow label="Fornitore">
            <input value={form.fornitore}
              onChange={(e) => {
                const v = e.target.value;
                set("fornitore", v);
                const match = fornitori.find(
                  (f) => (f.ragione_sociale || "").toLowerCase() === v.toLowerCase()
                );
                if (match && match.categoria && match.categoria !== form.categoria) {
                  // Nota: Costi Variabili usa un set di categorie diverso — usiamo il valore del fornitore
                  set("categoria", match.categoria);
                }
              }}
              data-testid="input-fornitore"
              list="cv-fornitori-datalist"
              autoComplete="off"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <datalist id="cv-fornitori-datalist">
              {fornitori.map((f) => (
                <option key={f.fornitore_id} value={f.ragione_sociale}>
                  {f.partita_iva ? `P.IVA ${f.partita_iva}` : ""}
                </option>
              ))}
            </datalist>
          </FormRow>
          <FormRow label="Data scadenza">
            <input type="date" required value={form.data_scadenza} onChange={(e) => set("data_scadenza", e.target.value)}
              data-testid="input-data-scadenza"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </FormRow>
          <FormRow label="Importo imponibile (€)">
            <input type="number" step="0.01" value={form.importo_imponibile} onChange={(e) => set("importo_imponibile", e.target.value)}
              data-testid="input-importo"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </FormRow>
          <FormRow label="Aliquota IVA (%)">
            <input type="number" step="0.5" value={form.aliquota_iva} onChange={(e) => set("aliquota_iva", e.target.value)}
              data-testid="input-iva"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </FormRow>
        </div>

        <div className="border-t border-slate-200 pt-3 mt-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Pagamento (opzionale)</div>
          <div className="grid md:grid-cols-3 gap-3">
            <FormRow label="Importo effettivo (€)">
              <input type="number" step="0.01" value={form.importo_effettivo}
                onChange={(e) => set("importo_effettivo", e.target.value)}
                placeholder="lascia vuoto = non pagata"
                data-testid="input-importo-effettivo"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </FormRow>
            <FormRow label="Data pagamento">
              <input type="date" value={form.data_pagamento} onChange={(e) => set("data_pagamento", e.target.value)}
                data-testid="input-data-pagamento"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </FormRow>
            <FormRow label="Metodo pagamento">
              <input value={form.metodo_pagamento} onChange={(e) => set("metodo_pagamento", e.target.value)}
                placeholder="es. Bonifico, Contanti"
                data-testid="input-metodo"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </FormRow>
          </div>
        </div>
        <FormRow label="Note">
          <textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)}
            data-testid="input-note"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </FormRow>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <button type="button" onClick={onClose}
            className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm">
            Annulla
          </button>
          <button type="submit" disabled={submitting}
            data-testid="btn-save"
            className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm disabled:opacity-60">
            {submitting ? "Salvataggio…" : (initial ? "Aggiorna" : "Crea")}
          </button>
        </div>
      </form>
    </div>
  );
}

const FormRow = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
    {children}
  </div>
);
